import type { Dispatch, SetStateAction } from "react";
import { X } from "lucide-react";
import type { Account, ModalBaseProps, TeamOption } from "./types";
import { POSITION_OPTIONS } from "./types";

interface EditAccountData {
  username: string;
  full_name: string;
  email: string;
  position: string;
  points: number;
  is_activated: boolean;
  can_self_request: boolean;
}

interface EditAccountModalProps extends ModalBaseProps {
  account: Account | null;
  editAccount: EditAccountData;
  setEditAccount: Dispatch<SetStateAction<EditAccountData>>;
  teams: TeamOption[];
  selectedEditTeamId: number | null | "REMOVE";
  setSelectedEditTeamId: Dispatch<SetStateAction<number | null | "REMOVE">>;
  approverTeamsToRemove: number[];
  setApproverTeamsToRemove: Dispatch<SetStateAction<number[]>>;
  approverTeamToAdd: number | null;
  setApproverTeamToAdd: Dispatch<SetStateAction<number | null>>;
  loading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  onSubmit: () => void;
}

export function EditAccountModal({
  isOpen,
  onClose,
  account,
  editAccount,
  setEditAccount,
  teams,
  selectedEditTeamId,
  setSelectedEditTeamId,
  approverTeamsToRemove,
  setApproverTeamsToRemove,
  approverTeamToAdd,
  setApproverTeamToAdd,
  loading,
  error,
  setError,
  onSubmit,
}: EditAccountModalProps) {
  if (!isOpen || !account) return null;

  const handleClose = () => {
    onClose();
    setError("");
  };

  // Whether the user is currently in a team and hasn't clicked Remove yet
  const inTeamNotRemoving =
    account.team_id != null && selectedEditTeamId !== "REMOVE" && typeof selectedEditTeamId !== "number";

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/30 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-account-title"
        className="bg-card rounded-lg shadow-2xl max-w-lg w-full border divide-y border-border divide-border max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-8">
          <div>
            <h2 id="edit-account-title" className="text-xl font-semibold">
              Edit Account
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update account details for {account.full_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="hover:opacity-70 transition-opacity"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 flex-1 overflow-y-auto min-h-0">
          {/* Credentials Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Credentials
            </h3>
            <div>
              <label
                htmlFor="edit-username"
                className="text-xs text-muted-foreground mb-2 block"
              >
                Username *
              </label>
              <input
                id="edit-username"
                type="text"
                value={editAccount.username}
                onChange={(e) =>
                  setEditAccount({ ...editAccount, username: e.target.value })
                }
                className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                placeholder="Enter username"
                aria-required="true"
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Personal Information
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="edit-fullName"
                  className="text-xs text-muted-foreground mb-2 block"
                >
                  Full Name *
                </label>
                <input
                  id="edit-fullName"
                  type="text"
                  value={editAccount.full_name}
                  onChange={(e) =>
                    setEditAccount({
                      ...editAccount,
                      full_name: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                  placeholder="Enter full name"
                  aria-required="true"
                />
              </div>

              <div>
                <label
                  htmlFor="edit-email"
                  className="text-xs text-muted-foreground mb-2 block"
                >
                  Email Address *
                </label>
                <input
                  id="edit-email"
                  type="email"
                  value={editAccount.email}
                  onChange={(e) =>
                    setEditAccount({ ...editAccount, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                  placeholder="Enter email address"
                  aria-required="true"
                />
              </div>
            </div>
          </div>

          {/* Role & Points Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Role & Points
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="edit-position"
                  className="text-xs text-muted-foreground mb-2 block"
                >
                  Position *
                </label>
                <select
                  id="edit-position"
                  value={editAccount.position}
                  onChange={(e) =>
                    setEditAccount({ ...editAccount, position: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                  aria-required="true"
                >
                  {POSITION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {editAccount.position === "Sales Agent" && (
                <>
                  <div>
                    <label
                      htmlFor="edit-points"
                      className="text-xs text-muted-foreground mb-2 block"
                    >
                      Points *
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        id="edit-points"
                        type="number"
                        min="0"
                        value={editAccount.points}
                        onChange={(e) =>
                          setEditAccount({
                            ...editAccount,
                            points: parseInt(e.target.value) || 0,
                          })
                        }
                        className="flex-1 px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                        placeholder="Enter points"
                        aria-required="true"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setEditAccount({
                            ...editAccount,
                            points: editAccount.points + 10,
                          })
                        }
                        aria-label="Add 10 points"
                        className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-muted border-border hover:bg-accent text-foreground"
                      >
                        +10
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setEditAccount({
                            ...editAccount,
                            points: editAccount.points + 100,
                          })
                        }
                        aria-label="Add 100 points"
                        className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-muted border-border hover:bg-accent text-foreground"
                      >
                        +100
                      </button>
                    </div>
                  </div>

                  {/* Team Section */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">
                      Team <span className="text-muted-foreground">(optional)</span>
                    </label>
                    {inTeamNotRemoving ? (
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          value={account.team_name ?? ""}
                          disabled
                          className="flex-1 px-4 py-3 rounded border cursor-not-allowed bg-muted border-border text-muted-foreground focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedEditTeamId("REMOVE")}
                          className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-red-600 hover:bg-red-700 text-white"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <select
                        value={
                          typeof selectedEditTeamId === "number"
                            ? selectedEditTeamId
                            : ""
                        }
                        onChange={(e) =>
                          setSelectedEditTeamId(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                      >
                        <option value="">No team assigned</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </>
              )}

              {editAccount.position === "Approver" && (
                <div>
                  <label
                    htmlFor="edit-approver-points"
                    className="text-xs text-muted-foreground mb-2 block"
                  >
                    Points *
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      id="edit-approver-points"
                      type="number"
                      min="0"
                      value={editAccount.points}
                      onChange={(e) =>
                        setEditAccount({
                          ...editAccount,
                          points: parseInt(e.target.value) || 0,
                        })
                      }
                      className="flex-1 px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                      placeholder="Enter points"
                      aria-required="true"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setEditAccount({
                          ...editAccount,
                          points: editAccount.points + 10,
                        })
                      }
                      aria-label="Add 10 points"
                      className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-muted border-border hover:bg-accent text-foreground"
                    >
                      +10
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setEditAccount({
                          ...editAccount,
                          points: editAccount.points + 100,
                        })
                      }
                      aria-label="Add 100 points"
                      className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-muted border-border hover:bg-accent text-foreground"
                    >
                      +100
                    </button>
                  </div>
                </div>
              )}

              {editAccount.position === "Approver" && (
                <div className="space-y-3">
                  <label className="text-xs text-muted-foreground block">
                    Manages Teams <span className="text-muted-foreground">(optional)</span>
                  </label>
                  {account.approver_teams && account.approver_teams.length > 0 && (
                    <div className="space-y-2">
                      {account.approver_teams.map((team) => {
                        const isPendingRemoval = approverTeamsToRemove.includes(team.id);
                        return (
                          <div
                            key={team.id}
                            className={`flex items-center gap-3${isPendingRemoval ? " opacity-50" : ""}`}
                          >
                            <input
                              type="text"
                              value={team.name}
                              disabled
                              className={`flex-1 px-4 py-3 rounded border cursor-not-allowed bg-muted border-border text-muted-foreground focus:outline-none${isPendingRemoval ? " line-through" : ""}`}
                            />
                            {isPendingRemoval ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setApproverTeamsToRemove(
                                    approverTeamsToRemove.filter((id) => id !== team.id),
                                  )
                                }
                                className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-muted hover:bg-accent border-border text-foreground"
                              >
                                Undo
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  setApproverTeamsToRemove([
                                    ...approverTeamsToRemove,
                                    team.id,
                                  ])
                                }
                                className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-red-600 hover:bg-red-700 text-white"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <select
                    id="approver-team-add"
                    value={approverTeamToAdd ?? ""}
                    onChange={(e) =>
                      setApproverTeamToAdd(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                    className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                  >
                    <option value="">No new team</option>
                    {teams
                      .filter(
                        (t) =>
                          !account.approver_teams?.some((at) => at.id === t.id) ||
                          approverTeamsToRemove.includes(t.id),
                      )
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {editAccount.position === "Approver" && (
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">
                    Member of Team <span className="text-muted-foreground">(optional)</span>
                  </label>
                  {account.team_id != null && selectedEditTeamId !== "REMOVE" && typeof selectedEditTeamId !== "number" ? (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={account.team_name ?? ""}
                        disabled
                        className="flex-1 px-4 py-3 rounded border cursor-not-allowed bg-muted border-border text-muted-foreground focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedEditTeamId("REMOVE")}
                        className="px-4 py-3 rounded border font-semibold text-sm transition-colors bg-red-600 hover:bg-red-700 text-white"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <select
                      value={
                        typeof selectedEditTeamId === "number"
                          ? selectedEditTeamId
                          : ""
                      }
                      onChange={(e) =>
                        setSelectedEditTeamId(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                      className="w-full px-4 py-3 rounded border bg-background border-border text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="">No team assigned</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    The team whose approver will review this user's self-requests
                  </p>
                </div>
              )}

              {editAccount.position === "Approver" && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editAccount.can_self_request}
                    onChange={(e) =>
                      setEditAccount({ ...editAccount, can_self_request: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-border"
                  />
                  <div>
                    <span className="text-sm text-foreground">Allow self-requests</span>
                    <p className="text-xs text-muted-foreground">
                      Enable this approver to create redemption requests for themselves
                    </p>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 border-t">
          {error && (
            <div className="w-full mb-4 p-3 g-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded text-sm">
              {error}
            </div>
          )}
          <button
            onClick={onSubmit}
            disabled={loading}
            className="w-full px-6 py-3 rounded-lg font-semibold transition-colors bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {loading ? "Updating..." : "Update Account"}
          </button>
        </div>
      </div>
    </div>
  );
}
